import { enhancedApi } from './generated/treatmentRecordRequestsService';

export const treatmentRecordRequestService = enhancedApi.enhanceEndpoints({
  addTagTypes: [
    'record-request',
    'record-request-channel',
    'record-request-audit-log',
    'record-request-audit-logs',
    'record-request-suggestions',
    'record-request-transitions',
    'record-request-requirements',
    'record-request-hipaa-package',
    'record-request-hipaa-authorization',
    'hipaa-package', // From hipaaPackageService - for cross-service invalidation
    'package-component', // From hipaaPackageService - for cross-service invalidation
  ],
  endpoints: {
    mattersProvidersRecordRequestsList: {
      providesTags: (result, error, { providerId }) => [
        { type: 'record-requests', id: providerId },
      ],
    },
    mattersProvidersRecordRequestsCreate: {
      invalidatesTags: (result, error, { providerId }) => [
        { type: 'record-requests', id: providerId },
        // New requests may have packages auto-created
        'hipaa-package',
      ],
    },
    mattersProvidersRecordRequestsRetrieve: {
      providesTags: (result) => [
        { type: 'record-request', id: result?.id },
        // Also provide package tag since they're 1-to-1
        ...(result?.id
          ? [{ type: 'hipaa-package' as const, id: `REQUEST-${result.id}` }]
          : []),
      ],
    },
    mattersProvidersRecordRequestsUpdate: {
      invalidatesTags: (result, error, { providerId }) => [
        { type: 'record-request', id: result?.id },
        { type: 'record-requests', id: providerId },
        'hipaa-package', // Request updates may affect package fields
        'package-component', // Components depend on request data
      ],
    },
    mattersProvidersRecordRequestsDestroy: {
      invalidatesTags: (result, error, { providerId }) => [
        { type: 'record-requests', id: providerId },
      ],
    },
    mattersProvidersRecordRequestsChangeStatusCreate: {
      invalidatesTags: (result, error, { providerId, requestId }) => [
        { type: 'record-request', id: requestId },
        { type: 'record-requests', id: providerId },
        { type: 'record-request-audit-logs', id: requestId },
        { type: 'record-request-suggestions', id: requestId },
        { type: 'record-request-transitions', id: requestId },
        'hipaa-package', // Status changes may affect package state
      ],
    },
    mattersProvidersRecordRequestsChannelsList: {
      providesTags: (result, error, { requestId }) => [
        { type: 'record-request-channels', id: requestId },
      ],
    },
    mattersProvidersRecordRequestsChannelsCreate: {
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'record-request-channels', id: requestId },
        { type: 'record-request', id: requestId },
        'hipaa-package', // Channel affects package missing fields
        'package-component', // Components may have treatmentChannelType/Value fields
      ],
    },
    mattersProvidersRecordRequestsChannelsRetrieve: {
      providesTags: (result) => [
        { type: 'record-request-channel', id: result?.id },
      ],
    },
    mattersProvidersRecordRequestsChannelsUpdate: {
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'record-request-channel', id: result?.id },
        { type: 'record-request-channels', id: requestId },
        { type: 'record-request', id: requestId },
        'hipaa-package', // Channel affects package missing fields
        'package-component', // Components may have treatmentChannelType/Value fields
      ],
    },
    mattersProvidersRecordRequestsChannelsDestroy: {
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'record-request-channels', id: requestId },
      ],
    },
    getHipaaEmailTemplate: {
      providesTags: (result, error, { requestId }) => [
        { type: 'record-request', id: requestId },
      ],
    },
    mattersProvidersRecordRequestsAuditLogsList: {
      providesTags: (result, error, { requestId }) => [
        { type: 'record-request-audit-logs', id: requestId },
      ],
    },
    mattersProvidersRecordRequestsAuditLogsRetrieve: {
      providesTags: (result) => [
        { type: 'record-request-audit-log', id: result?.id },
      ],
    },
    mattersProvidersRecordRequestsAuditLogsUpdate: {
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'record-request-audit-logs', id: requestId },
      ],
    },
    mattersProvidersRecordRequestsAuditLogsDestroy: {
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'record-request-audit-logs', id: requestId },
      ],
    },

    submitHipaaPackage: {
      invalidatesTags: (result, error, { providerId, requestId }) => [
        { type: 'record-request', id: requestId },
        { type: 'record-requests', id: providerId },
        { type: 'record-request-audit-logs', id: requestId },
        { type: 'record-request-suggestions', id: requestId },
        { type: 'record-request-transitions', id: requestId },
        'hipaa-package', // Package submission affects package state
        'package-component',
      ],
    },

    mattersHipaaAuthorizationCreate: {
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'record-request-hipaa-authorization', id: matterId },
        'hipaa-package', // Authorization affects package validation
        'package-component', // May affect clientHipaaSignedDate field
      ],
    },
    mattersHipaaAuthorizationUpdate: {
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'record-request-hipaa-authorization', id: matterId },
        'hipaa-package', // Authorization affects package validation
        'package-component', // May affect clientHipaaSignedDate field
      ],
    },
    mattersHipaaAuthorizationDestroy: {
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'record-request-hipaa-authorization', id: matterId },
        'hipaa-package', // Removing authorization affects package validation
        'package-component', // May affect clientHipaaSignedDate field
      ],
    },
    mattersHipaaAuthorizationForMatterRetrieve: {
      providesTags: (result, error, { matterId }) => [
        { type: 'record-request-hipaa-authorization', id: matterId },
      ],
    },
  },
});
